import { ILookupInfo, IPageProperty, NavSyncMode } from '@common/nav';
import { Observable } from '@common/nav/navmodel';
import { Bind as kBind } from '@common/navkendo/kutils';
import { IPersistentSetting } from '@common/services/settingsservice';
import { fn } from '@common/utils';
import { INavMethodConfig } from './navdata';
import { NavPageItem } from './navpageitem';
import { NavPropertyItem } from './navpropertyitem';

/** Wrapper holding current Value for a Property Item: Handles setting values from UI Level :
 * due to AutoUpdate Logic we dont want our UI Controls to Directly modify the Underlying kendo model
 * aggreagated Createion on Underlying Item
 * (or we could intercept them there ...)
 */
export class NavPropertyValue extends Observable {
    constructor(public readonly Property: IPageProperty, item?: NavPropertyItem) {
        super();

        if (!(Property && Property.Name))
            throw Error("No Property");

        this.SetCurrentItem(item);
    }

    /** Current Item, to be set by SetCurrentItem Only */
    public CurrentItem: NavPropertyItem;

    /** Set Current Item  */
    public SetCurrentItem(item?: NavPropertyItem) {
        if ((item = (item || null)) === this.CurrentItem)
            return;

        const evSetItem = "setItem";

        this.CurrentItem = item;
        this.trigger(evSetItem);

        let me = this,
            property = this.Property;

        if (item) {
            // Caution : these references are unset wOut Current Item
            var anchor = item.Root.Anchor,
                Mandatory = anchor.Mandatory,
                Readonly = anchor.Readonly,
                InVisible = anchor.InVisible,
                onNextSet = (handler: Function) => this.one(evSetItem, handler);

            // Register Change Events until next Item Change event gets fired
            onNextSet(Mandatory.RegisterChanged(property, _mandatoryChanged));
            onNextSet(Readonly.RegisterChanged(property, _readonlyChanged));
            onNextSet(InVisible.RegisterChanged(property, _hiddenChanged));
            onNextSet(item.RegisterPropertyChanged(property, _valueChanged));
        }

        // Initial refresh State
        _mandatoryChanged();
        _hiddenChanged();
        _readonlyChanged();
        _valueChanged();

        me.Title = property.Title;

        function _mandatoryChanged() {
            var newMandatory = Mandatory && Mandatory.GetValue(property, item);
            if (newMandatory !== me.Mandatory) {
                me.Mandatory = newMandatory;
                _valueChanged();
            }
        }

        function _readonlyChanged() {
            var newValue = property.ReadOnly || !item || (Readonly && Readonly.GetValue(property, item));
            if (me.ReadOnly != newValue) {
                me.ReadOnly = newValue;
                me.trigger("Readonly");
                _valueChanged();
            }
        }

        function _hiddenChanged() {
            if (!InVisible)
                return;
            var newHidden = InVisible.GetValue(property, item);
            if (me.Hidden != newHidden) {
                me.Hidden = newHidden;
                me.trigger("Hidden");
                _valueChanged();
            }
        }

        function _valueChanged() {
            var newValue = item ? me.GetValue(false) : undefined;
            if (newValue !== me.Value) {
                me.Value = newValue;
                me.trigger("Value");
            }

            var invalid = !me.Hidden && !!item && item.GetIsInvalid(property);
            if (invalid != me.Invalid) {
                me.Invalid = invalid;
                me.trigger("Invalid");
            }
        }
    }

    /** React on Current Item Change (called initially also) */
    public OnCurrentItemChanged(cb: () => any) {
        cb();
        return kBind(this, cb, "setItem");
    }

    /**
    * Get Value, returns null if no Current item Attached,
    * as undefined gets handles different in kendo controls (control.value(undefined) does get the value, not set it)
    */
    public GetValue(forDisplay?: boolean, rawValue?: boolean) {
        var item = this.CurrentItem;

        if (!item)
            return null;

        const prop = this.Property;

        var value = forDisplay ? item.GetDisplayValue(prop) : item.GetValue(prop);

        return rawValue === true ? value : item.Root.Service.PrepareClientValue(item, prop, value);
    }

    /**   Current Readonly State */
    public ReadOnly: boolean;

    /** Current Mandatory State */
    public Mandatory: boolean;

    /** Current Title of Item TODO : make Change aware! */
    public Title: string;

    /** Current Invalid State */
    public Invalid = false;

    /** Current Value */
    public Value = null;

    /** Current Visibility State  */
    public Hidden = false;

    /** React On Readonly Change, returns dispose Action */
    public OnReadonlyChange(cb: (isReadonly: boolean) => void) {
        cb(this.ReadOnly == true);
        var ac = () => cb(this.ReadOnly == true);
        return kBind(this, ac, "Readonly");
    }

    /** React On Invalid Change, returns dispose Action */
    public OnInvalidChange(cb: (isInvalid: boolean) => void) {
        cb(this.Invalid == true);
        var ac = fn.proxy(() => cb(this.Invalid == true), this);
        return kBind(this, ac, "Invalid");
    }

    /** Register Property Value Changed Event (use result for Deregistration) */
    public OnValueChange(cb: (value: any) => void) {
        var ac = () => cb(this.Value);
        return kBind(this, ac, "Value");
    }

    /** set Value, Handles User Notification in Case of Error! */
    public SetValue(value: any) {
        var item = this.CurrentItem;
        return item.SetValue(this.Property, value).then(fn.nullOp, err => {
            const cancelAfterShow = () => {
                fn.error.UICancel(err);
            };
            return item.Root.Service.$navui.ShowError(err).then(cancelAfterShow, cancelAfterShow);
        });
    }

    /** set lookup Value from lookup row (handling opt. additional lookupRow */
    public SetLookupFromRow(lookupRow: any, lookup: ILookupInfo) {
        let item = this.CurrentItem,
            root = item.Root,
            service = root.Service,
            customSet = lookup && lookup.CustomSetMethod && lookup.CustomSetMethod.Name && lookup.CustomSetMethod;

        if (!customSet) {
            let value = !lookupRow ? "" : lookupRow[lookup.TargetField];
            return this.SetLookupFromValue(value);
        }
        // Custom Set Method for Lookup based Field
        let p = customSet.Parameters.map(mParam => ({ Key: mParam, Value: (lookupRow && lookupRow[mParam]) || "" })),
            params = fn.ToDictionaryEx(p, x => x.Key, x => x.Value),
            methodConfig: INavMethodConfig = {
                MethodName: lookup.CustomSetMethod.Name,
                Parameters: params
            },
            sync = this.Property.SyncMode === NavSyncMode.Sync;

        // For Line Property : Convection: Client Keys for Line are additional Parameters
        if (item.MetaData.ParentProperty) {
            item.MetaData.KeyFields.forEach(key => params[key] = item.GetValue(key));
        }

        if (sync && item instanceof NavPageItem && item.Container) {
            items = fn.ToArray(item.Container.Items);
        } else {
            var items = [item];
        }

        let executeCustomSet = root.Anchor.EnqueueOperation(() => items.reduce((prev, curr) =>
            prev.then(() => root.ExecuteMethod(methodConfig, () =>
                methodConfig.Parameters[lookup.CustomSetMethod.KeyName] = curr.Root.Key, true))
            , service.$c.when<any>(undefined)));

        return executeCustomSet.then(undefined, err => service.$navui.ShowError(err));
    }

    public SetLookupFromValue(value: any) {
        // Standard SetValue for Lookup based Field
        let item = this.CurrentItem,
            isInt = this.Property.Type == "Int32";

        if (isInt && ("" + value == "0"))
            value = "";

        return item.SetValue(this.Property, value).then(fn.identity, (err) => {
            const cancelAfterShow = () => {
                fn.error.UICancel(err);
            };
            return item.Root.Service.$navui.ShowError(err).then(cancelAfterShow, cancelAfterShow);
        });
    }
}

/** Object with registration of Callbacks upon Disposing of a Context */
export interface INavEditContext {
    /** Add a Callback to be called Upon Disposal of Context */
    AddDispose(disposeCallback: () => void);

    /** Add Settings by name */
    AddSetting<T>(name: string, s: IPersistentSetting<T>): () => void;

    /** Lookup Control is generated via angular logic (?) , only for this we need angular scope
    *... and for this we need an elemnt alredy in dom ...
    */
    GetAttachedParent(): JQuery;

    /** only set for instanceof NavCellEditor */
    readonly IsCellEditor?: boolean;
}

declare global {
    interface JQuery {
        /** Custom 'EditContext' Panel */
        data(key: "INavEditContext"): INavEditContext;

        data(key: "INavEditContext", el: INavEditContext);
    }
}
